/**
 * Iter 165 · RiskDigestButton — compact popover for on/off + channel +
 * Send now action for the daily 09:00 IST risk digest.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { MailCheck, Send, Info } from "lucide-react";

export default function RiskDigestButton({ wsId }) {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  const load = useCallback(async () => {
    if (!wsId) return;
    try {
      const { data } = await api.get(`/workspaces/${wsId}/settings/risk-digest`);
      setCfg(data);
    } catch { /* keep placeholder */ }
  }, [wsId]);
  useEffect(() => { load(); }, [wsId, open, load]);

  const patch = async (partial) => {
    setBusy(true);
    try {
      const { data } = await api.patch(
        `/workspaces/${wsId}/settings/risk-digest`, partial);
      setCfg(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const sendNow = async (dryRun = false) => {
    setSending(true);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/tasks/risk-digest/send-now${dryRun ? "?dry_run=true" : ""}`);
      const n = data?.users_notified ?? 0;
      if (n === 0) {
        toast.success("No one has at-risk tasks right now — nothing to send.");
      } else if (dryRun) {
        toast.success(`Dry-run: ${n} teammate${n === 1 ? "" : "s"} would receive a digest`);
      } else {
        toast.success(`Digest sent to ${n} teammate${n === 1 ? "" : "s"}`);
      }
    } catch (e) { toast.error(apiErrorMessage(e, "Send-now failed")); }
    finally { setSending(false); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="h-8"
                 data-testid="risk-digest-btn">
          <MailCheck className="h-3.5 w-3.5 mr-1" /> Digest
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end" data-testid="risk-digest-popover">
        <div className="space-y-3">
          <div>
            <div className="flex items-center gap-2 text-sm font-bold">
              <MailCheck className="h-4 w-4" style={{ color: "hsl(var(--primary))" }} />
              Daily Risk Digest
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
              Every morning at 09:00 IST, we send each teammate a personalised
              email/WhatsApp with their top 3 at-risk tasks and the AI-generated
              next action for each.
            </p>
          </div>

          {cfg && (
            <>
              <div className="flex items-center justify-between border-t pt-3">
                <Label htmlFor="rd-enabled" className="text-xs">Enabled</Label>
                <Switch
                  id="rd-enabled"
                  checked={!!cfg.enabled}
                  onCheckedChange={(v) => patch({ enabled: v })}
                  disabled={busy}
                  data-testid="risk-digest-toggle"
                />
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Channel</Label>
                <Select value={cfg.channel || "email"}
                         onValueChange={(v) => patch({ channel: v })}>
                  <SelectTrigger className="h-8 text-xs mt-1" disabled={busy}
                                   data-testid="risk-digest-channel">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">📧 Email</SelectItem>
                    <SelectItem value="whatsapp">💬 WhatsApp</SelectItem>
                    <SelectItem value="both">📧 + 💬 Both</SelectItem>
                    <SelectItem value="off">Paused</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Uses your workspace Resend / Interakt keys (Settings → Integrations).
                </p>
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="rd-noassign" className="text-xs">
                  Include tasks with no assignee
                </Label>
                <Switch
                  id="rd-noassign"
                  checked={!!cfg.include_no_assignee}
                  onCheckedChange={(v) => patch({ include_no_assignee: v })}
                  disabled={busy}
                  data-testid="risk-digest-no-assignee"
                />
              </div>

              <div className="flex items-center gap-1 rounded-md bg-muted/60 p-2 text-[11px] text-muted-foreground">
                <Info className="h-3 w-3 shrink-0" />
                <span>Scheduled at 03:30 UTC daily (= 09:00 IST).</span>
              </div>

              <div className="flex items-center gap-2 border-t pt-3">
                <Button size="sm" variant="outline" onClick={async () => {
                  setPreviewBusy(true);
                  try {
                    const { data } = await api.get(
                      `/workspaces/${wsId}/tasks/risk-digest/preview-me`);
                    setPreview(data);
                  } catch (e) { toast.error(apiErrorMessage(e, "Preview failed")); }
                  finally { setPreviewBusy(false); }
                }}
                         disabled={previewBusy} className="flex-1"
                         data-testid="risk-digest-preview-me">
                  {previewBusy ? "…" : "Preview mine"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => sendNow(true)}
                         disabled={sending} className="flex-1"
                         data-testid="risk-digest-preview">
                  {sending ? "…" : "Dry-run all"}
                </Button>
                <Button size="sm" onClick={() => sendNow(false)}
                         disabled={sending} className="flex-1"
                         data-testid="risk-digest-sendnow">
                  <Send className="h-3 w-3 mr-1" />
                  {sending ? "…" : "Send now"}
                </Button>
              </div>

              {preview && (
                <div className="border-t pt-3" data-testid="risk-digest-preview-card">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                      What you would receive tomorrow morning
                    </div>
                    <button type="button" onClick={() => setPreview(null)}
                             className="text-muted-foreground hover:text-foreground text-xs">×</button>
                  </div>
                  <div className="rounded-md border border-border bg-muted/30 p-3 text-xs space-y-1">
                    <div className="font-semibold">{preview.subject}</div>
                    {preview.recipient && (
                      <div className="text-[10px] text-muted-foreground">To: {preview.recipient}</div>
                    )}
                    <div className="whitespace-pre-line text-foreground/80 font-mono text-[11px] leading-relaxed pt-2 border-t border-border/60">
                      {preview.body}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
